import React, { useState } from 'react';
import { usePortal } from '../../contexts/PortalContext';
import type { Show } from '../../types/portal';
import { BUTTON_QUIET, BUTTON_SOLID, FIELD } from './adminStyles';

/** Matches components/portal/ProjectFields.tsx's own FieldGroup exactly —
    small enough that sharing it isn't worth a new file. */
function FieldGroup({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  return <div className="mt-10 first:mt-0" data-id="element-1457" data-mp="2fdz9c8j0obez">
      <span className="label block text-paper/25" data-id="element-1458" data-mp="2fh4ytovk5fb8">{label}</span>
      <div className="mt-5 grid grid-cols-1 gap-7 sm:grid-cols-2" data-id="element-1459" data-mp="2kemxmmfv2e5i">{children}</div>
    </div>;
}

/**
 * Edits the show's own title, plus the handful of fields shared by every
 * episode of it — client name, project type, client contact, description —
 * from one place. Reached either from the Show Admin page itself or from any
 * one episode's Project Settings, since a show with only one episode so far
 * has no separate Show Admin page to visit yet.
 *
 * Saving writes the show's own row. Client name, type, contact and
 * description are also mirrored onto every episode under it (PortalContext's
 * `updateShow`), so nothing there is ever specific to one episode — that's
 * what Edit Project is for. Show Name is the one field NOT mirrored: it is
 * the show's own title, not an episode's — episodes keep their own separate
 * names (e.g. "Episode 1") throughout.
 */
export function ShowFields({
  show,
  onClose
}: {
  show: Show;
  onClose: () => void;
}) {
  const {
    updateShow,
    notify
  } = usePortal();
  const [draft, setDraft] = useState<Show>(show);
  function edit(patch: Partial<Show>) {
    setDraft(current => ({
      ...current,
      ...patch
    }));
  }
  function save(event: React.FormEvent) {
    event.preventDefault();
    updateShow(show.id, {
      name: draft.name.trim() || show.name,
      client: draft.client.trim() || show.client,
      type: draft.type,
      description: draft.description,
      clientContact: draft.clientContact
    });
    notify('Show details updated · every episode kept in sync');
    onClose();
  }
  return <form onSubmit={save} className="mt-10 border border-paper/25 px-6 py-8 sm:px-8" data-id="element-1460" data-mp="mzdf5k9fpf8n">
      <h3 className="font-display text-2xl leading-none" data-id="element-1461" data-mp="n16leyg4xxg4">Edit Show</h3>
      <p className="label-sm mt-3 max-w-md leading-relaxed text-paper/30" data-id="element-1462" data-mp="ryyehvsinplw">
        Shared across every episode of {show.name} — saving here updates all of them at once.
      </p>

      <div className="mt-8" data-id="element-1463" data-mp="3vg88a80fjl9j">
        {/* The show's own title — e.g. "Economy of Scale" — is not shared
            with episodes (each episode keeps its own name, e.g. "Episode 1")
            and so is not mirrored by `updateShow`, unlike every field below. */}
        <FieldGroup label="Show" data-id="element-1464" data-mp="d0xlpamxptvz">
          <label className="block" data-id="element-1465" data-mp="d1z8gcdbc4tj">
            <span className="label block text-paper/40" data-id="element-1466" data-mp="d3a7xzzq6qeu">Show Name</span>
            <input value={draft.name} onChange={event => edit({
            name: event.target.value
          })} className={FIELD} data-id="element-1467" data-mp="3co5vj7qwsp4k" />
          </label>
        </FieldGroup>

        <FieldGroup label="Client" data-id="element-1468" data-mp="395yo433pfgl6">
          <label className="block" data-id="element-1469" data-mp="347nb5ji44pyq">
            <span className="label block text-paper/40" data-id="element-1470" data-mp="348q4rz6tl6ic">Client Name</span>
            <input value={draft.client} onChange={event => edit({
            client: event.target.value
          })} className={FIELD} data-id="element-1471" data-mp="305k0gx39ppei" />
          </label>
          <label className="block" data-id="element-1472" data-mp="2q7m23grp2c86">
            <span className="label block text-paper/40" data-id="element-1473" data-mp="2q8npmkpwcr6s">Project Type</span>
            <input value={draft.type} onChange={event => edit({
            type: event.target.value
          })} placeholder="e.g. Event Coverage" className={FIELD} data-id="element-1474" data-mp="2lgnia77hyw06" />
          </label>
        </FieldGroup>

        <FieldGroup label="Client Contact" data-id="element-1475" data-mp="2ceea3nzpbepy">
          <label className="block" data-id="element-1476" data-mp="27hxp32nstgh3">
            <span className="label block text-paper/40" data-id="element-1477" data-mp="27guykvue1g1p">Main Client Contact</span>
            <input value={draft.clientContact} onChange={event => edit({
            clientContact: event.target.value
          })} placeholder="e.g. Sarah Jones · Marketing Director" className={FIELD} data-id="element-1478" data-mp="22jco310q2srl" />
          </label>
        </FieldGroup>

        <div className="mt-10" data-id="element-1479" data-mp="3stpwv4q9yp23">
          <span className="label block text-paper/25" data-id="element-1480" data-mp="3suhhxlm8x4e4">Description</span>
          <label className="mt-5 block" data-id="element-1481" data-mp="1nesozpyx86j">
            <span className="sr-only" data-id="element-1482" data-mp="1m3mkt91voc9">Show Description</span>
            <textarea value={draft.description} onChange={event => edit({
            description: event.target.value
          })} rows={3} placeholder="e.g. A three-part leadership series filmed at the client's Dubai office." className={`${FIELD} resize-none`} data-id="element-1483" data-mp="3jt9vq1x07u0e" />
          </label>
        </div>
      </div>

      <div className="mt-10 flex flex-wrap items-center gap-6" data-id="element-1484" data-mp="1840jyx94tdc1">
        <button type="submit" className={BUTTON_SOLID} data-id="element-1485" data-mp="182frdd6do4gt">
          Save Show
        </button>
        <button type="button" onClick={onClose} className={BUTTON_QUIET} data-id="element-1486" data-mp="1d1itaor29ukk">
          Cancel
        </button>
      </div>
    </form>;
}